import { store } from '../redux/store';
import {
  sseConnected,
  sseDisconnected,
  sseEventReceived,
} from '../redux/slices/sse/sseSlice';

const SSE_BASE_URL = process.env.REACT_APP_SSE_URL || '/api/sse';

/** SSE event names the backend sends. Add new events here. */
const SSE_EVENTS = [
  'player-joined',
  'game-status',
  'quiz-sync',
  'quiz-result',
  'leaderboard-update',
  'geo-sync',
  'geo-result',
  'sketcher-current-round',
  'sketcher-round-result',
  'zero-sketch-report',
] as const;

/**
 * Singleton that owns the EventSource connection.
 * Lives outside React — never torn down by component lifecycle.
 * All events are dispatched to Redux; screens read via selectors.
 *
 * connect(gameId)  — open connection (idempotent for same gameId)
 * reconnect()      — force a fresh connection, discarding any stale/dead one
 * close()          — explicitly tear down when leaving a game
 *
 * Mobile resume handling: when the screen locks or the tab is backgrounded,
 * the browser can freeze the page and drop the EventSource without reviving
 * it. We listen for visibilitychange / pageshow / online and force a
 * reconnect when the socket is found dead on resume. This applies to every
 * screen (host and player) because they all share this singleton.
 */
class SseManager {
  private source: EventSource | null = null;
  private gameId: string | null = null;
  private listenersBound = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  /** Open a connection for a game. Idempotent for the same gameId. */
  connect(gameId: string) {
    if (this.source && this.gameId === gameId) return;
    this.gameId = gameId;
    this.open();
    this.bindLifecycle();
  }

  /**
   * (Re)open a fresh EventSource, discarding any stale/dead one.
   * Bypasses the connect() idempotency guard — this is what makes
   * resume-after-lock actually reconnect.
   */
  private open() {
    if (!this.gameId) return;
    this.closeSource();
    const gameId = this.gameId;
    const source = new EventSource(`${SSE_BASE_URL}/stream/${gameId}`);
    this.source = source;

    source.onopen = () => {
      this.clearReconnectTimer();
      store.dispatch(sseConnected(gameId));
    };

    source.onerror = () => {
      // Native EventSource retries while CONNECTING; only step in if it has
      // given up (CLOSED), which is the mobile-freeze case.
      if (source.readyState === EventSource.CLOSED) {
        this.scheduleReconnect();
      }
    };

    // Register all known event types (named events with `event:` field)
    SSE_EVENTS.forEach((event) => {
      source.addEventListener(event, (e: Event) => {
        const data = JSON.parse((e as MessageEvent).data);
        store.dispatch(sseEventReceived({ event, data }));
      });
    });

    // Fallback: handle unnamed events that carry a type/event field in the JSON body
    source.onmessage = (e: MessageEvent) => {
      const data = JSON.parse(e.data);
      const event = data.event || data.type;
      if (event && SSE_EVENTS.includes(event)) {
        store.dispatch(sseEventReceived({ event, data: data.data ?? data }));
      }
    };
  }

  /** Force a reconnect regardless of current (possibly dead) state. */
  reconnect() {
    if (!this.gameId) return;
    this.open();
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnect();
    }, 1000);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /** Bind resume/online listeners once per manager lifetime. */
  private bindLifecycle() {
    if (this.listenersBound) return;
    this.listenersBound = true;
    document.addEventListener('visibilitychange', this.onResume);
    window.addEventListener('online', this.onResume);
    window.addEventListener('pageshow', this.onResume);
  }

  // Arrow fn → stable reference for add/removeEventListener and bound `this`.
  private onResume = () => {
    if (!this.gameId) return;
    // Ignore when going to the background.
    if (document.visibilityState === 'hidden') return;
    // Only reconnect if the socket is actually gone/dead; leave a healthy
    // stream alone.
    if (!this.source || this.source.readyState === EventSource.CLOSED) {
      this.reconnect();
    }
  };

  private closeSource() {
    this.source?.close();
    this.source = null;
  }

  /** Explicit teardown when leaving a game. */
  close() {
    this.clearReconnectTimer();
    this.closeSource();
    this.gameId = null;
    if (this.listenersBound) {
      document.removeEventListener('visibilitychange', this.onResume);
      window.removeEventListener('online', this.onResume);
      window.removeEventListener('pageshow', this.onResume);
      this.listenersBound = false;
    }
    store.dispatch(sseDisconnected());
  }
}

export const sseManager = new SseManager();
